// @flow

/*
 ** Imports
 */

// Modules
import React, { type Node } from 'react';
import styled, { css } from 'styled-components';

import Icon from 'components/icon';
import { PRIMARY_DARK, BLUE_GREY, WHITE, DARK } from 'src/styles/palettes';

// Assets
import { ChevronDownThin } from 'assets/svg';

const DROPDOWN_LIST_PADDING = 2;

/*
 ** Styled
 */

const DropdownWrapper: any = styled.div`
  flex: 1;
  height: 100%;
  ${(props) => props.disabled && `opacity: 0.4;`}
`;

export const DropdownRoot: any = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  padding: 0.4em 0.7em;
  background-color: ${BLUE_GREY[600]};
  color: ${WHITE};
  font-size: 1em;
  font-weight: 500;
  border-radius: 6px;
`;

const Label: any = styled.div`
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  flex: 1;
  margin-right: 0.5em;
  color: #fff;
`;

const ColorLabel: any = styled.div`
  min-width: 65px;
  height: 7px;
  margin-right: 0.5em;
  border-radius: 4px;
  border: 1px solid ${DARK[700]};
  background-color: ${(props) => props.color};
`;

const ChevronIcon: any = styled(ChevronDownThin)`
  width: 1em;
  height: 1em;
  transition: 0.3s;
  ${(props) =>
    props.visible &&
    css`
      transform: scaleY(-1);
    `}
`;

export const DropdownLabel: any = styled.div`
  cursor: ${(props) => (props.disabled ? 'inherit' : 'pointer')};
  display: flex;
  height: 100%;
  min-height: 1em;
  flex-flow: row nowrap;
  justify-content: space-between;
  align-items: center;
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;

  color: ${(props) => (props.disabled ? WHITE : PRIMARY_DARK)};
`;

export const DropdownListRoot: any = styled.div`
  position: absolute;
  width: 100%;
  left: 0;
  padding-top: 5px;
  background: ${BLUE_GREY[600]};
  border-radius: 0px 0px 6px 6px;
  z-index: 1000;
  border-top-width: 0px;
`;

export const DropdownList: any = styled.div`
  display: inline-flex;
  flex-flow: column;
  flex-wrap: wrap;
  width: 100%;
  max-height: ${(props) => props.listMaxHeight || 400}px;
  padding: ${DROPDOWN_LIST_PADDING}px;
  overflow-y: hidden;
  overflow-x: visible;
`;

export const DropdownItemRoot: any = styled.div`
  position: relative;
  display: flex;
  padding: 0.5em;
  min-height: 25px;
  height: 25px;
  width: 100%;
  margin: 1px 0px;
  align-items: center;
  color: ${WHITE}};
  background-color: ${(props) => (props.selected ? BLUE_GREY[500] : BLUE_GREY[600])};
  cursor: pointer;

  :last-child {
    border-radius: 0px 0px 2px 2px;
  }

  :hover {
    background-color: ${BLUE_GREY[500]};
  }
`;

export const DropdownItemLabel: any = styled.div`
  width: 100%;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

/*
 ** Types
 */

type PropTypes = {
  children: Node,
  className?: string,
  selectedItems?: Array<any>,
  icon?: string,
  color?: ?string,
  disabled?: boolean,
  label?: string,
  isColorSelected?: boolean,
  position?: string,
  listMaxHeight?: number,
  hideOnSelect: boolean,
  onItemClick?: (any) => void,
  onDropdownHide?: () => void
};

type ItemPropTypes = {
  value?: any,
  label?: string,
  children?: Node,
  selected?: boolean,
  disabled?: boolean,
  onSelect?: (value: any) => void,
  onClick?: (value: any) => void
};

type StateTypes = {
  visible: boolean
};

/*
 ** Component
 */

export default class Dropdown extends React.Component<PropTypes, StateTypes> {
  rootElement: any = React.createRef();

  constructor(props: PropTypes) {
    super(props);
    this.state = {
      visible: false
    };
  }

  static Item: any = ({ value, label, onSelect, selected, disabled, onClick, ...props }: ItemPropTypes) => (
    <DropdownItemRoot selected={selected} disabled={disabled} onClick={onClick || (onSelect && onSelect(value))}>
      <DropdownItemLabel>{label || props.children || value}</DropdownItemLabel>
    </DropdownItemRoot>
  );

  handleSelected: any = (selectedValue?: string) => () => {
    const { onItemClick, hideOnSelect } = this.props;

    onItemClick && onItemClick(selectedValue);

    if (hideOnSelect) {
      this.toggleVisibility();
    }
  };

  toggleVisibility: any = () => {
    this.setState((prevState) => {
      const { onDropdownHide } = this.props;

      if (prevState.visible && onDropdownHide) {
        onDropdownHide();
      }

      return { visible: !prevState.visible };
    });
  };

  handleClickOnDocument: any = (event: MouseEvent) => {
    const { rootElement } = this;
    const { visible } = this.state;
    const { onDropdownHide } = this.props;

    if (visible && rootElement && !rootElement.contains(event.target)) {
      this.setState(() => ({
        visible: false
      }));
      if (onDropdownHide) {
        onDropdownHide();
      }
    }
  };

  componentDidMount() {
    document.addEventListener('click', this.handleClickOnDocument);
  }

  componentWillUnmount() {
    document.removeEventListener('click', this.handleClickOnDocument);
  }

  render(): Node {
    const { children, icon, disabled, label, position, className, listMaxHeight, isColorSelected } = this.props;
    const { visible } = this.state;
    const dropdownLabel = label;

    return (
      <DropdownWrapper className={className} disabled={disabled}>
        <DropdownRoot
          visible={visible}
          disabled={disabled}
          onDoubleClick={(e) => e.stopPropagation()}
          onClick={() => !disabled && !visible && this.toggleVisibility()}
          ref={(ref) => (this.rootElement = ref)}>
          <DropdownLabel
            onClick={() => !disabled && visible && this.toggleVisibility()}
            onDoubleClick={(e) => e.stopPropagation()}
            visible={visible}
            disabled={disabled}>
            {dropdownLabel && (isColorSelected ? <ColorLabel color={dropdownLabel} /> : <Label>{dropdownLabel}</Label>)}
            {icon && <Icon asset={icon} size={'1em'} color={'#fff'} />}
            <ChevronIcon visible={visible ? 1 : null} />
          </DropdownLabel>
          {visible && (
            <DropdownListRoot key="dropdownListRoot" position={position || 'bottom'} listMaxHeight={listMaxHeight}>
              <DropdownList listMaxHeight={listMaxHeight}>
                {React.Children.map(children, (child) =>
                  React.cloneElement(child, {
                    onSelect: this.handleSelected,
                    selected: child.props.selected
                  })
                )}
              </DropdownList>
            </DropdownListRoot>
          )}
        </DropdownRoot>
      </DropdownWrapper>
    );
  }
}
